<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SzNewMessageModal from './SzNewMessageModal.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import { DuplicateContactException, ExceptionWithMessage } from 'shared/helpers/CustomErrors';

const store = useStore();
const { t } = useI18n();
const showNewMsgModal = ref(false);
const createNewContactDialogRef = ref(null);
const accountId = computed(() => store.getters.getCurrentAccountId);

async function openNewContactDialog() {
  await createNewContactDialogRef.value?.contactsFormRef?.resetValidation();
  createNewContactDialogRef.value?.dialogRef?.open();
}

async function onCreateContact(contact) {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();
    useAlert(t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      else if (error.data.includes('phone_number')) useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
}
</script>

<template>
  <div class="sz-empty-state">
    <!-- Illustration -->
    <div class="sz-empty-illustration">
      <div class="sz-empty-ring-outer">
        <div class="sz-empty-ring-inner">
          <div class="sz-empty-icon-center">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#388E3C" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <circle cx="8" cy="10" r="0.5" fill="#388E3C" stroke="#388E3C" stroke-width="1"/>
              <circle cx="12" cy="10" r="0.5" fill="#388E3C" stroke="#388E3C" stroke-width="1"/>
              <circle cx="16" cy="10" r="0.5" fill="#388E3C" stroke="#388E3C" stroke-width="1"/>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Text -->
    <div class="sz-empty-text">
      <h3>NENHUMA CONVERSA SELECIONADA</h3>
      <p>Selecione uma conversa ou envie uma nova atividade.</p>
    </div>

    <!-- Action cards -->
    <div class="sz-empty-cards">
      <!-- Nova mensagem -->
      <div class="sz-empty-card" @click="showNewMsgModal = true">
            <div class="sz-empty-card-icon sz-icon-green">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <circle cx="8" cy="10" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
                <circle cx="12" cy="10" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
                <circle cx="16" cy="10" r="0.5" fill="currentColor" stroke="currentColor" stroke-width="1"/>
              </svg>
            </div>
            <div class="sz-empty-card-body">
              <div class="sz-empty-card-title">Nova mensagem</div>
              <div class="sz-empty-card-desc">Envie uma mensagem para um contato existente</div>
            </div>
            <div class="sz-empty-card-chevron">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
      </div>
      <SzNewMessageModal v-if="showNewMsgModal" @close="showNewMsgModal = false" />

      <!-- Novo contato -->
      <div class="sz-empty-card" @click="openNewContactDialog">
        <div class="sz-empty-card-icon sz-icon-purple">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M16 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <circle cx="8.5" cy="7" r="4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M20 8v6M23 11h-6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="sz-empty-card-body">
          <div class="sz-empty-card-title">Novo contato</div>
          <div class="sz-empty-card-desc">Cadastre um novo contato no sistema</div>
        </div>
        <div class="sz-empty-card-chevron">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
    </div>
    <CreateNewContactDialog ref="createNewContactDialogRef" @create="onCreateContact" />
</div>
</template>

<style scoped>
.sz-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 32px;
  gap: 24px;
  font-family: Inter, system-ui, sans-serif;
  box-sizing: border-box;
}

/* Illustration rings */
.sz-empty-illustration { position: relative; }
.sz-empty-ring-outer {
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: rgba(56, 142, 60, 0.04);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-empty-ring-inner {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(56, 142, 60, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-empty-icon-center {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(56, 142, 60, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Text */
.sz-empty-text { text-align: center; }
.sz-empty-text h3 {
  font-size: 15px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 8px;
  letter-spacing: 0.5px;
}
.sz-empty-text p {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
}

/* Cards */
.sz-empty-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 380px;
}
.sz-empty-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.sz-empty-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* Icon */
.sz-empty-card-icon {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.sz-icon-green {
  background: rgba(56, 142, 60, 0.08);
  color: #388E3C;
}
.sz-icon-purple {
  background: rgba(124, 77, 255, 0.08);
  color: #7C4DFF;
}

/* Body */
.sz-empty-card-body { flex: 1; min-width: 0; }
.sz-empty-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 2px;
}
.sz-empty-card-desc {
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.4;
}

/* Chevron */
.sz-empty-card-chevron {
  flex-shrink: 0;
  color: #d1d5db;
}

/* Dark mode */
.dark .sz-empty-text h3 { color: #f3f4f6; }
.dark .sz-empty-text p { color: #6b7280; }
.dark .sz-empty-card { background: #1D1E21; border-color: #363840; }
.dark .sz-empty-card:hover { border-color: #525252; box-shadow: 0 2px 12px rgba(0,0,0,0.2); }
.dark .sz-empty-card-title { color: #f3f4f6; }
.dark .sz-empty-card-desc { color: #6b7280; }
.dark .sz-empty-card-chevron { color: #4b5563; }
.dark .sz-empty-ring-outer { background: rgba(74, 222, 128, 0.04); }
.dark .sz-empty-ring-inner { background: rgba(74, 222, 128, 0.06); }
.dark .sz-empty-icon-center { background: rgba(74, 222, 128, 0.1); }
</style>
